import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { errorToasterMessage } from "@/utils/functions/toaster";
import StackButton from "@/components/buttons/StackButton";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import StatusToggle from "@/components/toggles/StatusToggle";
import { UserAssignmentEditSkeleton } from "@/components/skeletons";
import { fetchAllRoles } from "@/services/role.service";
import { getUserById, updateUserById } from "@/services/user.service";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_ROLES, ERROR_MESSAGES } from "@/utils/constants/constants";
import { setDataInLocalStorage } from "@/utils/functions/helperFunctions";

function UpdateUserRoles() {
    const [allRoles, setAllRoles] = useState<any[]>([]);
    const [userDetails, setUserDetails] = useState<any>({});
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [loader, setLoader] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const id = params.id || "";

    useEffect(() => {
        let cancelled = false;

        setIsPageLoading(true);
        setAllRoles([]);

        getUserById(
            id,
            (response: any) => {
                if (cancelled) return;

                const user = response.data;
                setUserDetails(user);

                fetchAllRoles(
                    (rolesResponse: any) => {
                        if (cancelled) return;

                        setDataInLocalStorage(ALL_ROLES, rolesResponse.data.roles);
                        const rolesList = rolesResponse.data.roles.map((role: any) => ({
                            ...role,
                            checked: user?.organizations?.[0]?.roles?.some(
                                (obj: { name: string }) => obj.name === role.name
                            )
                                ? true
                                : false,
                        }));
                        setAllRoles(rolesList);
                        setIsPageLoading(false);
                    },
                    (error: any) => {
                        if (cancelled) return;
                        console.log(error?.response?.data?.message);
                        setIsPageLoading(false);
                    }
                );
            },
            (error: any) => {
                if (cancelled) return;
                console.log(error);
                setIsPageLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
    }, [id]);

    const onChangeRole = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRoleList = allRoles.map((role) => {
            if (e.target.name === role.name) {
                return { ...role, checked: e.target.checked };
            }
            return role;
        });
        setAllRoles(newRoleList);
    };

    const handleUpdateUser = () => {
        const newRoleList = allRoles.filter((role) => role.checked);
        const newRoleIdList = newRoleList.map((role) => role.id);

        if (newRoleIdList.length > 0) {
            const data = JSON.stringify({ roles: newRoleIdList });
            setLoader(true);
            updateUserById(
                id,
                data,
                (response: any) => {
                    setLoader(false);
                    if (response.status === 200) navigate(-1);
                },
                (error: any) => {
                    console.log(error);
                    errorToasterMessage(error.data.message);
                    setLoader(false);
                }
            );
        } else {
            errorToasterMessage(ERROR_MESSAGES.NO_ROLE_SELECTED);
        }
    };

    if (isPageLoading) {
        return <UserAssignmentEditSkeleton sectionTitle="Roles" />;
    }

    return (
        <div className="gs-form-page">
            <div className="gs-form-page-header">
                <h3 className="gs-form-page-title min-w-0 truncate" id="edit-user-role-email-address-title">
                    {userDetails?.email_address}
                </h3>
                <StackButton
                    secondaryVariant="light"
                    primaryId="edit-user-role-save-btn"
                    secondaryId="edit-user-role-cancel-btn"
                    primaryButton={BUTTONS.SAVE}
                    secondaryButton={BUTTONS.CANCEL}
                    handlePrimary={handleUpdateUser}
                    handleSecondary={() => navigate(-1)}
                    isLoading={loader}
                />
            </div>

            <div className="gs-form-page-section">
                <h5 className="gs-form-section-title gs-form-section-title--spaced" id="edit-user-role-title">
                    Roles
                </h5>
                <div className="gs-list-page-card">
                    <ul className="gs-detail-list gs-form-toggle-list">
                        {allRoles.length > 0 ? (
                            allRoles.map((role, key) => (
                                <li className="gs-detail-list-item" key={role.id}>
                                    <StatusToggle
                                        index={key}
                                        onChange={onChangeRole}
                                        name={role.name}
                                        id={role.id}
                                        checked={role.checked}
                                        disabled={role.name === "superadmin"}
                                    >
                                        <p className="m-0" id={`edit-user-role-name-label-${key}`}>
                                            {role.name}
                                        </p>
                                    </StatusToggle>
                                </li>
                            ))
                        ) : (
                            <li className="gs-detail-list-item">
                                <NoDataAvailable>
                                    <p className="m-0" id="edit-user-role-no-data">
                                        No roles available
                                    </p>
                                </NoDataAvailable>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default UpdateUserRoles;
